import { useEffect } from 'react';
import type { UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import type { ConvertToTaskFormValues } from '../schemas';
import type { Tag } from '@/features/tags/public';

interface UseConvertToTaskFieldSyncArgs {
  setValue: UseFormSetValue<ConvertToTaskFormValues>;
  getValues: UseFormGetValues<ConvertToTaskFormValues>;
  projectId: string;
  boardId: string;
  setSelectedTags: (tags: Tag[]) => void;
  onlyBoard: string | null;
  onlyStatus: string | null;
  onlyProject: string | null;
  proposedProject: string | null;
  proposedStatus: string | null;
  proposedGroup: string | null;
  proposedAssignee: string | null;
}

/**
 * Clears and preselects the fields that depend on `projectId`/`boardId`.
 *
 * Order matters, and it is the whole reason these are grouped here. Effects run in
 * the order they are declared, so everything that CLEARS a field is declared before
 * everything that PRESELECTS one: on a render where both apply — the projects arrive
 * and the dialog resets in the same commit — the clear used to run last and wipe the
 * preselect, leaving the only project there is unselected and the assignee the note
 * named cleared right after being set.
 */
export function useConvertToTaskFieldSync({
  setValue,
  getValues,
  projectId,
  boardId,
  setSelectedTags,
  onlyBoard,
  onlyStatus,
  onlyProject,
  proposedProject,
  proposedStatus,
  proposedGroup,
  proposedAssignee,
}: UseConvertToTaskFieldSyncArgs) {
  // Clear dependent fields when project changes — boards, statuses and tags are
  // all project-scoped, so a leftover selection would point at another project.
  useEffect(() => {
    setValue('boardId', '');
    setValue('tags', []);
    setSelectedTags([]);
  }, [projectId, setValue, setSelectedTags]);

  // Statuses and groups belong to the board, not the project.
  useEffect(() => {
    setValue('statusId', '');
    setValue('groupId', null);
  }, [boardId, setValue]);

  // Clear assignee when project changes (assignee might not be in new project)
  useEffect(() => {
    setValue('assigneeId', null);
  }, [projectId, setValue]);

  // Every preselect below writes only into an empty field, so nothing the user picked is ever
  // overwritten, and the proposal wins over the single-option default because it runs on the same
  // empty field with a more specific value.
  //
  // Each one also depends on the field whose change clears it — project for the board, board for
  // the status and the group. The clear runs on the commit after the selection lands, so without
  // that dependency the preselect fired once, was wiped, and never came back: with a single board
  // the form was left invalid and Crear tarea did nothing.
  useEffect(() => {
    const next = proposedProject ?? onlyProject;
    if (next && !getValues('projectId')) setValue('projectId', next);
  }, [proposedProject, onlyProject, getValues, setValue]);

  useEffect(() => {
    if (onlyBoard && !getValues('boardId')) setValue('boardId', onlyBoard);
  }, [onlyBoard, projectId, getValues, setValue]);

  useEffect(() => {
    const next = proposedStatus ?? onlyStatus;
    if (next && !getValues('statusId')) setValue('statusId', next);
  }, [proposedStatus, onlyStatus, boardId, getValues, setValue]);

  useEffect(() => {
    if (proposedGroup && !getValues('groupId')) setValue('groupId', proposedGroup);
  }, [proposedGroup, boardId, getValues, setValue]);

  // `projectId` is a dependency so this re-applies after the clear above, which runs on the
  // commit *after* this one when a project is picked. Without it the assignee the note named was
  // set once and then wiped for good. Re-applying is also the right rule: `memberOptions` is
  // scoped to the chosen project, so a person who is not a member of it resolves to null here and
  // the field stays empty — which is exactly why the clear exists.
  useEffect(() => {
    if (proposedAssignee && !getValues('assigneeId')) setValue('assigneeId', proposedAssignee);
  }, [proposedAssignee, projectId, getValues, setValue]);
}
